﻿import TKTable from '@tk/components/tk.table';
import group from '@tk/utilities/tk.array.group';
import { fetchRequest } from '@tk/utilities/tk.fetch';
import render from '@tk/utilities/tk.render';

export default class KWTable extends TKTable {
    asyncURL?: string;
    observeBodies: boolean;
    mutationObserver: MutationObserver;
    isStickyRight: boolean;
    stickyRightClassname: string;
    leftShadowClassName: string;
    addURLParam: boolean;
    widthObserver: MutationObserver;

    constructor() {
        super();

        this.asyncURL = this.getAttribute('data-tk-async-url') || undefined;
        this.observeBodies = this.hasAttribute('data-tk-observe-bodies');
        this.mutationObserver = new MutationObserver(KWTable.handleMutation.bind(this));

        this.isStickyRight = this.hasAttribute('data-tk-is-sticky-right');
        this.stickyRightClassname = this.getAttribute('data-tk-sticky-right-class-name') || 'tk-table--sticky-right';
        this.leftShadowClassName = this.getAttribute('data-tk-left-shadow-class-name') || 'tk-table__left-shadow';
        this.addURLParam = this.hasAttribute('data-tk-urlparam');

        this.widthObserver = new MutationObserver(this.checkOverflow.bind(this));
    }

    connectedCallback(): void {
        super.connectedCallback();

        if (this.asyncURL) this.getData();
        if (!this.asyncURL && this.observeBodies) this.initMutationObserver();

        this.widthObserver.observe(this, { attributes: true, childList: true, subtree: true });
        this.checkOverflow();
    }

    registerScrollListener() {
        super.registerScrollListener();

        if (this.isStickyRight) {
            const handleStickyRightListener = this.toggleScrollRightBehavior.bind(this);
            this.pushListener({
                event: 'scroll',
                element: this,
                action: handleStickyRightListener,
            });
        }
    }

    toggleScrollRightBehavior(event: Event) {
        const element = event.target as HTMLElement;
        const isAtScrollEnd = element.scrollLeft + element.clientWidth + 1 >= element.scrollWidth;
        this.classList.toggle(this.stickyRightClassname, !isAtScrollEnd);
    }

    checkOverflow() {
        if (this.isStickyRight && this.clientWidth < this.scrollWidth) {
            this.classList.add(this.stickyRightClassname);
            this.widthObserver.disconnect();
        }
    }

    getData() {
        fetchRequest({
            requestURL: this.asyncURL!,
            resolveHandler: this.handleResponse.bind(this),
        });
    }

    initMutationObserver() {
        if (!this.table) return;

        const config = {
            attributes: true,
            attributeFilter: ['hidden'],
        } as MutationObserverInit;

        const filteredList = Array.from(this.table.rows).filter((element) => element.closest('tbody'));

        Array.from(this.table.tBodies).forEach((element) => {
            KWTable.toggleBody(element);
        });

        filteredList.forEach((element) => {
            this.mutationObserver.observe(element, config);
        });
    }

    handleResponse(response: TKResponse) {
        if (!response || !response.success) return;
        const table = render<HTMLTableElement>(response.dataAsHtml);
        if (!table) return;
        const rowList = Array.from(table.rows).map((item) => ({
            value: item.getAttribute('data-tk-group-by'),
            element: item,
        }));
        const grouped = group(rowList, 'value');

        Object.keys(grouped).forEach((keys) => {
            if (!this.table) return;
            const rows = grouped[keys];
            const tBody = this.table.createTBody();
            tBody.classList.add('tk-table__body');
            tBody.setAttribute('data-tk-table-body', '');
            rows.forEach((row) => {
                tBody.insertAdjacentElement('beforeend', row.element);
            });
        });

        const productVariants = this.closest('kw-product-variants');
        if (!productVariants) return;
        productVariants.setAttribute('data-tk-ready-to-run', 'true');

        const articleList = this.closest('tk-article-list');
        if (!articleList) return;
        const prices = articleList.querySelectorAll('[data-tk-price-wrapper]');
        prices.forEach((element) => element.setAttribute('data-tk-ready-to-run', 'true'));
        const stocks = articleList.querySelectorAll('[data-tk-stock-wrapper]');
        stocks.forEach((element) => element.setAttribute('data-tk-ready-to-run', 'true'));

        if (this.observeBodies) this.initMutationObserver();
    }

    static handleMutation(mutationList: MutationRecord[]) {
        mutationList.forEach((mutation) => {
            const row = mutation.target as HTMLTableRowElement;
            const tBody = row.closest('tbody');
            tBody && KWTable.toggleBody(tBody);
        });
    }

    static toggleBody(tBody: HTMLTableSectionElement) {
        const rowList = Array.from(tBody.rows);
        const allRowsHidden = rowList.every((element) => element.hidden);
        tBody.hidden = allRowsHidden;
    }

    handleRowLink(event: Event) {
        const target = event.target as HTMLElement;
        if (target.closest('a')
        || target.closest('button')
        || target.closest('input')
        || target.closest('select')
        || target.closest('kw-basket-button')) return;
        const row = target.closest(`[${this.rowLinkSelector}]`);
        if (!row) return;
        let link = row.getAttribute(this.rowLinkSelector);
        if (!link) return;

        if (this.addURLParam) {
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.delete('prodart');
            const newQueryString = urlParams.toString();
            link = `${link}&${newQueryString}`;
        }

        window.location.href = link;
    }
}
